import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { Camera, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { formatDateTime } from '@/lib/dates'

// Mirrors the GET /api/vehicles/:id/mileage-history response shape.
// `source` is validated server-side against the four-value CHECK
// constraint; treating it as a union here keeps the source-chip
// rendering exhaustive at compile time.
type MileageSource = 'manual' | 'photo' | 'eric_extracted' | 'invoice'

type MileageHistoryRow = {
  id: string
  mileage_km: number
  source: MileageSource
  photo_r2_key: string | null
  appointment_id: string | null
  captured_at: string
  captured_by_clerk_user_id: string | null
  notes: string | null
}

type MileageHistoryResponse = {
  rows: MileageHistoryRow[]
  limit: number
  offset: number
  has_more: boolean
}

const INITIAL_LIMIT = 10
const EXPANDED_LIMIT = 50

function sourceChipClass(source: MileageSource): string {
  // §4.9: capture source is a fact → neutral chip. The one exception is
  // assistant attribution (§2 green list): eric_extracted rows carry the
  // saved-family wash so the assistant lane stays recognizable.
  switch (source) {
    case 'eric_extracted':
      return 'border-saved-line bg-saved-soft text-saved-deep'
    case 'photo':
    case 'invoice':
    case 'manual':
    default:
      return 'border-border bg-muted text-muted-foreground'
  }
}

export function MileageHistoryPanel({ vehicleId }: { vehicleId: string }) {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const limit = expanded ? EXPANDED_LIMIT : INITIAL_LIMIT

  const historyQuery = useQuery({
    queryKey: ['vehicle', vehicleId, 'mileage-history', { limit }],
    enabled: isLoaded && !!vehicleId,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch(
        `/api/vehicles/${vehicleId}/mileage-history?limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as MileageHistoryResponse
    },
  })

  const rows = historyQuery.data?.rows ?? []
  const showExpandToggle =
    rows.length >= INITIAL_LIMIT || expanded || historyQuery.data?.has_more

  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-foreground">
          {t('detail.mileage_history.header')}
        </h2>
        {rows.length > 0 ? (
          <p className="text-xs text-muted-foreground">
            {t('detail.mileage_history.subtitle_count', { count: rows.length })}
          </p>
        ) : null}
      </div>

      <div className="rounded-[14px] border border-border bg-background">
        {historyQuery.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('detail.mileage_history.loading')}
          </div>
        ) : historyQuery.isError ? (
          <div className="px-4 py-10 text-center text-sm text-destructive">
            {t('detail.mileage_history.error')}
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            {t('detail.mileage_history.empty')}
          </div>
        ) : (
          <>
            {/* Desktop table — md+ only. Each row renders in one line so
                the timeline scans top-to-bottom without wrapping. */}
            <table className="hidden w-full text-sm md:table">
              <thead className="border-b border-border text-left text-[12px] text-ink-3">
                <tr>
                  <th className="px-4 py-2 font-medium">
                    {t('detail.mileage_history.column.date')}
                  </th>
                  <th className="px-4 py-2 text-right font-medium">
                    {t('detail.mileage_history.column.km')}
                  </th>
                  <th className="px-4 py-2 font-medium">
                    {t('detail.mileage_history.column.source')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-ink-3">
                      {formatDateTime(row.captured_at)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right font-mono font-semibold text-foreground">
                      {tCommon('units.km', {
                        value: row.mileage_km.toLocaleString(),
                      })}
                    </td>
                    <td className="px-4 py-2">
                      <SourceChip source={row.source} />
                      {row.photo_r2_key ? (
                        <Camera
                          aria-label={t('detail.mileage_history.from_photo_aria')}
                          className="ml-2 inline size-3.5 align-text-bottom text-ink-3"
                        />
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile stacked-card view — cards mirror the table columns
                top-to-bottom so the same timeline reads on a phone
                without horizontal scroll. */}
            <div className="divide-y divide-border md:hidden">
              {rows.map((row) => (
                <div key={row.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-base font-semibold text-foreground">
                      {tCommon('units.km', {
                        value: row.mileage_km.toLocaleString(),
                      })}
                    </div>
                    <div className="mt-0.5 font-mono text-xs text-ink-3">
                      {formatDateTime(row.captured_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <SourceChip source={row.source} />
                    {row.photo_r2_key ? (
                      <Camera
                        aria-label={t('detail.mileage_history.from_photo_aria')}
                        className="size-3.5 text-ink-3"
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {showExpandToggle ? (
              <div className="border-t border-border px-4 py-2 text-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpanded((v) => !v)}
                  className="min-h-11 text-xs text-muted-foreground"
                >
                  {expanded
                    ? t('detail.mileage_history.show_less')
                    : t('detail.mileage_history.show_more')}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

function SourceChip({ source }: { source: MileageSource }) {
  const { t } = useTranslation('vehicles')
  return (
    <span
      className={`inline-flex items-center rounded-full border px-[9px] py-[3px] text-[12px] font-[650] ${sourceChipClass(source)}`}
    >
      {t(`detail.mileage_history.source.${source}`)}
    </span>
  )
}
